// /admin/catering — catering bookings (kitchen flow, Stage 2B). A booking is
// its own record, never a till order: the event, dishes at menu or special
// price, a discount, deposits and payments, a branded invoice / receipt, and
// "Send to kitchen" which makes the event's one production plan.
// Strings: lib/inventoryFlow/cateringStrings.ts. Rules: lib/inventoryFlow/cateringLogic.ts.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Plus } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { useKitchenFlowGate } from '@/hooks/useKitchenFlowEnabled'
import { formatMoney } from '@/lib/currency'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn, formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { T } from '@/lib/inventoryFlow/strings'
import { PT } from '@/lib/inventoryFlow/planStrings'
import { CT, CXT } from '@/lib/inventoryFlow/cateringStrings'
import type { CateringBooking } from '@/lib/inventoryFlow/cateringTypes'
import {
  balanceBadge, bookingListParams, canWriteBookings, filterBookings, listChip, type BookingFilter, type ListChip,
} from '@/lib/inventoryFlow/cateringLogic'
import { BookingForm, Field } from './BookingForm'
import { BookingChip, BookingView, localToday } from './BookingView'

type Notify = (kind: 'success' | 'error', message: string) => void
type View = { kind: 'list' } | { kind: 'edit'; booking: CateringBooking | null } | { kind: 'detail'; id: string } | { kind: 'report' }

const KEY = ['inventory-flow', 'catering'] as const

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message.split(' — ')[0] : 'Something went wrong'
}

const money = (n: number) => formatMoney(n)

export function CateringPage() {
  const { enabled, isLoading, isError, retry } = useKitchenFlowGate()
  if (isLoading) return <div className="p-6 text-muted-foreground">{T.loading}</div>
  if (isError) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground">{T.settingsUnreachable}</p>
        <Button onClick={retry}>{T.retryNow}</Button>
      </div>
    )
  }
  if (!enabled) return <div className="p-6 text-muted-foreground">{T.notEnabled}</div>
  return <CateringScreen />
}

function CateringScreen() {
  const role = getStoredUserRole()
  const { toast } = useToast()
  const notify: Notify = (kind, message) => toast({ title: message, variant: kind === 'error' ? 'destructive' : undefined })
  const [view, setView] = useState<View>({ kind: 'list' })
  const writer = canWriteBookings(role)
  const seesMoney = writer || role === 'accountant'

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{CT.title}</h1>
          <p className="text-muted-foreground">{CT.subtitle}</p>
        </div>
        {view.kind === 'list' && (
          <div className="flex gap-2">
            {seesMoney && <Button variant="outline" onClick={() => setView({ kind: 'report' })}>{CT.report}</Button>}
            {writer && <Button onClick={() => setView({ kind: 'edit', booking: null })}><Plus className="mr-2 h-4 w-4" /> {CT.newBooking}</Button>}
          </div>
        )}
      </div>
      {view.kind === 'list' && <BookingList onOpen={(id) => setView({ kind: 'detail', id })} />}
      {view.kind === 'edit' && (
        <BookingForm booking={view.booking} notify={notify}
          onDone={(b) => setView(b ? { kind: 'detail', id: b.id } : view.booking ? { kind: 'detail', id: view.booking.id } : { kind: 'list' })} />
      )}
      {view.kind === 'detail' && (
        <BookingView id={view.id} role={role} notify={notify} onBack={() => setView({ kind: 'list' })} onEdit={(b) => setView({ kind: 'edit', booking: b })} />
      )}
      {view.kind === 'report' && <PaymentsReport onBack={() => setView({ kind: 'list' })} />}
    </div>
  )
}

// ── list ────────────────────────────────────────────────────────────────────

function BalancePill({ booking }: { booking: CateringBooking }) {
  if (booking.status === 'cancelled' && booking.totals.paid < 0.005) return null
  const b = balanceBadge(booking.status === 'cancelled' ? { ...booking.totals, balance: -booking.totals.paid } : booking.totals)
  const cls = b.kind === 'due' ? 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100'
    : b.kind === 'refund' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
      : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100'
  const label = b.kind === 'due' ? CT.balanceDue(money(b.amount)) : b.kind === 'refund' ? CT.owedBack(money(b.amount)) : CT.paidUp
  return <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold', cls)}>{label}</span>
}

const CHIP_CLS: Record<ListChip['kind'], string> = {
  send: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  resend: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  confirm: 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100',
  eventPassed: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  close: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  due: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100',
  refund: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  withKitchen: 'bg-muted text-muted-foreground',
}

/** The row's next step: "Send to kitchen", "Rs 46,900 due", "Event passed — mark delivered". */
function NextChip({ chip }: { chip: ListChip }) {
  const label = chip.kind === 'due' ? CXT.chip.due(money(chip.amount ?? 0))
    : chip.kind === 'refund' ? CXT.chip.refund(money(chip.amount ?? 0))
      : CXT.chip[chip.kind]
  return <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold', CHIP_CLS[chip.kind])}>{label}</span>
}

function BookingList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<BookingFilter>('upcoming')
  const today = localToday()
  // Each tab asks the server for its own slice (lib bookingListParams);
  // Unpaid has no date limit, so money due never drops out of view.
  const params = useMemo(() => bookingListParams(filter, today), [filter, today])
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [...KEY, 'list', params],
    queryFn: () => apiClient.getCateringBookings(params),
  })
  const list = filterBookings(data?.data ?? [], filter, today)
  return (
    <div className="space-y-4">
      <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="tablist">
        {(['upcoming', 'unpaid', 'past', 'cancelled'] as const).map((f) => (
          <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)}
            className={cn('flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors',
              filter === f ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            {CT.filters[f]}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-muted-foreground">{T.loading}</p>
      ) : isError ? (
        <div className="space-y-3">
          <p className="text-destructive">{CT.loadFailed}</p>
          <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>{T.retryNow}</Button>
        </div>
      ) : list.length === 0 ? (
        <p className="text-muted-foreground">{CT.noBookings}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {list.map((b) => (
            <li key={b.id}>
              <button type="button" onClick={() => onOpen(b.id)} className="flex w-full flex-wrap items-center gap-3 p-4 text-left hover:bg-muted/50 touch-manipulation">
                <div className="min-w-[8rem]">
                  <div className="text-base font-semibold">{formatDateHuman(b.event_date)}</div>
                  {b.event_time && <div className="text-sm text-muted-foreground">{b.event_time}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{b.event_name ? `${b.event_name} — ${b.customer_name}` : b.customer_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {b.booking_no} · {PT.portions(b.portion_count)}{b.guest_count ? ` · ${CT.guests(b.guest_count)}` : ''} · {money(b.totals.total)}
                  </div>
                </div>
                <RowChips booking={b} today={today} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function RowChips({ booking: b, today }: { booking: CateringBooking; today: string }) {
  const chip = listChip(b, today)
  // A money chip already says what is owed; the balance pill would repeat it.
  const money = chip?.kind === 'due' || chip?.kind === 'refund'
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chip && <NextChip chip={chip} />}
      {!money && <BalancePill booking={b} />}
      <BookingChip status={b.status} />
    </div>
  )
}

// ── payments report ─────────────────────────────────────────────────────────

function PaymentsReport({ onBack }: { onBack: () => void }) {
  const today = localToday()
  const [from, setFrom] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 29)
    return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(today)
  const { data, isLoading, error } = useQuery({ queryKey: [...KEY, 'payments', from, to], queryFn: () => apiClient.getCateringPaymentsReport({ from, to }) })
  const r = data?.data
  return (
    <div className="space-y-4">
      <Button variant="ghost" className="px-2" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {CT.back}</Button>
      <h2 className="text-xl font-bold">{CT.reportTitle}</h2>
      <div className="flex flex-wrap gap-3">
        <Field label={CT.from}><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label={CT.to}><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      {error && <p className="text-destructive">{errMessage(error)}</p>}
      {isLoading || !r ? null : (
        <>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[[CT.received, r.received], [CT.refunded, r.refunded], [CT.net, r.net],
              ...(['cash', 'bank_transfer', 'card'] as const).map((m) => [CT.methods[m], r.by_method[m] ?? 0] as const)].map(([label, v]) => (
              <div key={String(label)} className="rounded-lg border p-3">
                <div className="text-sm text-muted-foreground">{label}</div>
                <div className="text-lg font-bold tabular-nums">{money(Number(v))}</div>
              </div>
            ))}
          </div>
          {r.payments.length === 0 ? <p className="text-muted-foreground">{CT.noPayments}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr><th className="p-2">{CT.when}</th><th className="p-2">{CT.booking}</th><th className="p-2">{CT.kind}</th><th className="p-2">{CT.method}</th><th className="p-2 text-right">{CT.amount}</th></tr>
                </thead>
                <tbody className="divide-y">
                  {r.payments.map((p) => (
                    <tr key={p.id}>
                      <td className="p-2 tabular-nums">{formatDateTimeHuman(p.received_at)}</td>
                      <td className="p-2">{p.booking_no} · {p.customer_name}</td>
                      <td className="p-2">{CT.kinds[p.kind]}</td>
                      <td className="p-2">{CT.methods[p.method]}{p.reference ? ` · ${p.reference}` : ''}</td>
                      <td className="p-2 text-right font-semibold tabular-nums">{p.kind === 'refund' ? '−' : ''}{money(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{CT.catering}</p>
        </>
      )}
    </div>
  )
}
